import React, { useState } from 'react';
import { auth } from '../firebase';
import { createReport } from '../utils/secureFunctions';

interface ReportModalProps {
  contentId: string;
  contentType: 'post' | 'comment' | 'testimony' | 'pulse' | 'groupPost' | 'message';
  reportedUserId: string;
  onClose: () => void;
}

const reportReasons = [
  'Spam or misleading',
  'Harassment or bullying',
  'Inappropriate content',
  'Hate speech',
  'Violence or dangerous organizations',
  'False information',
  'Something else'
];

const ReportModal: React.FC<ReportModalProps> = ({ contentId, contentType, reportedUserId, onClose }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason || !auth.currentUser) return;

    setSubmitting(true);
    try {
      await createReport({
        contentId,
        contentType,
        reportedUserId,
        reason: selectedReason,
        additionalInfo,
      });

      alert('Report submitted successfully. Our team will review it.');
      onClose();
    } catch (error) {
      console.error('Failed to submit report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Report Content</h3>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>Help us understand what's happening with this content.</p>

          <div style={styles.section}>
            <label style={styles.label}>Why are you reporting this?</label>
            {reportReasons.map((reason) => (
              <div key={reason} style={styles.radioOption}>
                <input
                  type="radio"
                  id={reason}
                  name="reason"
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  style={styles.radio}
                />
                <label htmlFor={reason} style={styles.radioLabel}>{reason}</label>
              </div>
            ))}
          </div>

          <div style={styles.section}>
            <label style={styles.label}>Additional information (optional)</label>
            <textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Provide more details..."
              style={styles.textarea}
              rows={4}
            />
          </div>

          <button 
            onClick={handleSubmit} 
            disabled={!selectedReason || submitting} 
            style={styles.submitButton}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '2px solid #E0E0E0',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#d32f2f',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#5B6E6E',
    padding: '0',
  },
  content: {
    padding: '20px',
  },
  description: {
    fontSize: '14px',
    color: '#5B6E6E',
    marginBottom: '20px',
  },
  section: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontWeight: '600',
    color: '#072B2C',
    fontSize: '15px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  radio: {
    marginRight: '8px',
    cursor: 'pointer',
  },
  radioLabel: {
    fontSize: '14px',
    color: '#072B2C',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '2px solid #E0E0E0',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  submitButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#d32f2f',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '10px',
  },
};

export default ReportModal;
